import { useEffect, useState } from 'react';
import { getAdminCatalogGroups, getAdminCatalogGroupVariationById } from '../../../shared/api/products.services';

const accessoryOptionsCache = new Map();
const accessoryOptionsInFlight = new Map();

const getAccessoryOptionsCacheKey = (searchQuery) => String(searchQuery || '').trim().toLowerCase();

// Суммарный остаток на складе по всем офферам карточки (onHand - reserved).
const sumGroupStock = (offers = []) =>
    (Array.isArray(offers) ? offers : []).reduce((total, offer) => {
        const stocks = Array.isArray(offer?.stocks) ? offer.stocks : [];
        const offerQty = stocks.reduce(
            (sum, s) => sum + Math.max(0, Number(s?.onHand || 0) - Number(s?.reserved || 0)),
            0
        );
        return total + offerQty;
    }, 0);

// Лейбл опции допродажи: название карточки + суммарный остаток.
export const getAccessoryOptionLabel = (accessory = {}) => {
    const groupTitle = accessory?.productGroup?.title?.ua
        || accessory?.productGroup?.title?.en
        || accessory?.title?.ua
        || accessory?.title?.en
        || accessory?.groupTitle
        || '';
    const stock = accessory?.totalStock;
    const base = groupTitle || String(accessory?.groupId || accessory?.productGroup?._id || '').trim();
    if (base && Number.isFinite(Number(stock))) return `${base} (на складі: ${Number(stock)})`;
    return base;
};

// Хук для загрузки КАРТОЧЕК товаров для допродажу (с суммарным остатком).
export function useAccessoryOptions(accessorySearchQuery) {
    const [accessoryOptions, setAccessoryOptions] = useState([{ value: '', label: 'Без допродажу' }]);
    const [isAccessorySearchLoading, setIsAccessorySearchLoading] = useState(false);

    useEffect(() => {
        let isCancelled = false;
        const normalizedSearchQuery = String(accessorySearchQuery || '').trim();
        const cacheKey = getAccessoryOptionsCacheKey(normalizedSearchQuery);

        const applyOptions = (nextOptions) => {
            if (!isCancelled) {
                setAccessoryOptions(nextOptions);
            }
        };

        const cachedOptions = accessoryOptionsCache.get(cacheKey);
        if (cachedOptions) {
            applyOptions(cachedOptions);
            setIsAccessorySearchLoading(false);
            return () => {
                isCancelled = true;
            };
        }

        const inFlightRequest = accessoryOptionsInFlight.get(cacheKey);
        if (inFlightRequest) {
            setIsAccessorySearchLoading(true);
            inFlightRequest.then(applyOptions).finally(() => {
                if (!isCancelled) setIsAccessorySearchLoading(false);
            });
            return () => {
                isCancelled = true;
            };
        }

        const loadAccessoryOptions = async (searchQuery) => {
            const nextOptionsMap = new Map();
            nextOptionsMap.set('', 'Без допродажу');

            try {
                const result = await getAdminCatalogGroups({
                    page: 1,
                    limit: 100,
                    q: searchQuery || undefined,
                });
                const groups = result?.items || [];

                await Promise.all(
                    groups.map(async (group) => {
                        const groupId = String(group?.groupId || group?._id || '');
                        if (!groupId || nextOptionsMap.has(groupId)) return;

                        const offersResult = await getAdminCatalogGroupVariationById(groupId, {
                            page: 1,
                            limit: 200,
                            compact: 1,
                        });
                        const offerItems = offersResult?.items || offersResult?.item?.offers || [];

                        const label = getAccessoryOptionLabel({
                            productGroup: group,
                            groupId,
                            totalStock: sumGroupStock(offerItems),
                        });
                        nextOptionsMap.set(groupId, label || groupId);
                    })
                );

                return Array.from(nextOptionsMap, ([value, label]) => ({ value, label }));
            } catch (error) {
                return Array.from(nextOptionsMap, ([value, label]) => ({ value, label }));
            }
        };

        setIsAccessorySearchLoading(true);
        const requestPromise = loadAccessoryOptions(normalizedSearchQuery);
        accessoryOptionsInFlight.set(cacheKey, requestPromise);

        requestPromise
            .then((nextOptions) => {
                accessoryOptionsCache.set(cacheKey, nextOptions);
                applyOptions(nextOptions);
            })
            .finally(() => {
                accessoryOptionsInFlight.delete(cacheKey);
                if (!isCancelled) setIsAccessorySearchLoading(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [accessorySearchQuery]);

    return { accessoryOptions, isAccessorySearchLoading };
}
